// PropertyMapPanel.tsx
// Displays Street View or Static Map depending on property type.
// Shows AVM estimate and cycle/signal badges overlaid on the image.

import { useState } from 'react'
import { getMapMode, getPropertyTypeConfig } from '../../lib/htag-property-type'

interface PropertyMapPanelProps {
  property: {
    address: string
    suburb: string
    state: string
    postcode: string
    latitude?: number | null
    longitude?: number | null
    property_type: string
  }
  avm_low?: number | null
  avm_mid?: number | null
  avm_high?: number | null
  cycle_phase?: string | null
  investment_signal?: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtAVM(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}m`
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`
  return `$${n.toLocaleString()}`
}

interface BadgeStyle {
  background: string
  color: string
  dot: string
}

function cycleBadgeStyle(phase: string): BadgeStyle {
  const p = phase.toLowerCase()
  if (p.includes('peak') && p.includes('+')) return { background: '#FEF3C7', color: '#92400E', dot: '#D97706' }
  if (p.includes('peak') && p.includes('-')) return { background: '#FEE2E2', color: '#991B1B', dot: '#DC2626' }
  if (p.includes('peak')) return { background: '#FEF3C7', color: '#92400E', dot: '#D97706' }
  if (p.includes('rising') || p.includes('bottom') && p.includes('+')) return { background: '#D1FAE5', color: '#065F46', dot: '#16A34A' }
  if (p.includes('falling') || p.includes('peak') && p.includes('-')) return { background: '#FEE2E2', color: '#991B1B', dot: '#DC2626' }
  if (p.includes('bottom')) return { background: '#DBEAFE', color: '#1E40AF', dot: '#2563EB' }
  return { background: '#F3F4F6', color: '#374151', dot: '#6B7280' }
}

function signalBadgeStyle(signal: string): BadgeStyle {
  switch (signal.toUpperCase()) {
    case 'HOLD': return { background: '#D1FAE5', color: '#065F46', dot: '#16A34A' }
    case 'ACCUMULATE': return { background: '#DBEAFE', color: '#1E40AF', dot: '#2563EB' }
    case 'WATCH': return { background: '#FEF3C7', color: '#92400E', dot: '#D97706' }
    case 'SELL': return { background: '#FEE2E2', color: '#991B1B', dot: '#DC2626' }
    default: return { background: '#F3F4F6', color: '#374151', dot: '#6B7280' }
  }
}

function Badge({ label, style }: { label: string; style: BadgeStyle }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: style.background,
      color: style.color,
      fontSize: 11,
      fontWeight: 500,
      padding: '3px 8px',
      borderRadius: 4,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: style.dot, display: 'inline-block' }} />
      {label}
    </span>
  )
}

// ─── Placeholder (no lat/lng or no API key) ───────────────────────────────────

function MapPlaceholder({ address, suburb, state }: { address: string; suburb: string; state: string }) {
  return (
    <div style={{
      height: 220,
      width: '100%',
      background: '#E8E3D8',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6B6560',
    }}>
      <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{ marginBottom: 8, opacity: 0.5 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
      </svg>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{address}</span>
      <span style={{ fontSize: 11, marginTop: 2, opacity: 0.7 }}>{suburb}, {state}</span>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PropertyMapPanel({
  property,
  avm_low,
  avm_mid,
  avm_high,
  cycle_phase,
  investment_signal,
}: PropertyMapPanelProps) {
  const [imgError, setImgError] = useState(false)

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
  const { latitude, longitude, property_type, address, suburb, state, postcode } = property
  const hasCoords = typeof latitude === 'number' && typeof longitude === 'number'
  const mapMode = getMapMode(property_type)
  const config = getPropertyTypeConfig(property_type)

  let mapUrl: string | null = null
  if (apiKey && !imgError) {
    if (hasCoords) {
      const lat = latitude as number
      const lng = longitude as number
      if (mapMode === 'streetview') {
        mapUrl = `https://maps.googleapis.com/maps/api/streetview?size=800x220&location=${lat},${lng}&fov=90&pitch=0&key=${apiKey}`
      } else {
        mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=16&size=800x220&maptype=roadmap&markers=color:0xC8761A|${lat},${lng}&style=feature:poi|visibility:off&key=${apiKey}`
      }
    } else {
      // Fallback: use address string as map center when lat/lng not yet geocoded
      const addressStr = encodeURIComponent(`${address}, ${suburb} ${state} ${postcode}, Australia`)
      if (mapMode === 'streetview') {
        mapUrl = `https://maps.googleapis.com/maps/api/streetview?size=800x220&location=${addressStr}&fov=90&pitch=0&key=${apiKey}`
      } else {
        mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${addressStr}&zoom=16&size=800x220&maptype=roadmap&markers=color:0xC8761A|${addressStr}&style=feature:poi|visibility:off&key=${apiKey}`
      }
    }
  }

  const showMap = mapUrl !== null

  return (
    <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #E8E3D8', background: '#fff' }}>
      {/* ── Map / Street View image ── */}
      <div style={{ position: 'relative', height: 220, overflow: 'hidden' }}>
        {showMap ? (
          <img
            src={mapUrl!}
            alt={`${address}, ${suburb}`}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <MapPlaceholder address={address} suburb={suburb} state={state} />
        )}

        {/* Overlay badges — top right */}
        {(cycle_phase || investment_signal) && (
          <div style={{
            position: 'absolute',
            top: 10,
            right: 10,
            display: 'flex',
            gap: 6,
          }}>
            {cycle_phase && (
              <Badge label={cycle_phase} style={cycleBadgeStyle(cycle_phase)} />
            )}
            {investment_signal && (
              <Badge label={investment_signal} style={signalBadgeStyle(investment_signal)} />
            )}
          </div>
        )}
      </div>

      {/* No-data notice for commercial/other */}
      {config.availability === 'none' && (
        <div style={{
          fontSize: 11,
          color: '#9CA3AF',
          fontStyle: 'italic',
          padding: '8px 20px',
          borderBottom: '1px solid #E8E3D8',
        }}>
          Market intelligence is available for residential properties. Location shown for reference.
        </div>
      )}

      {/* ── Details bar ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 20px',
        borderTop: '1px solid #E8E3D8',
        background: '#fff',
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#1A1A1A' }}>{address}</div>
          <div style={{ fontSize: 12, color: '#6B6560', marginTop: 2 }}>
            {suburb} {state} {postcode}
            {property_type && (
              <span style={{ marginLeft: 8, textTransform: 'capitalize' }}>· {property_type.replace(/_/g, ' ')}</span>
            )}
          </div>
        </div>

        {avm_mid != null && (
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 500, color: '#1A1A1A' }}>{fmtAVM(avm_mid)}</div>
            {avm_low != null && avm_high != null && (
              <div style={{ fontSize: 11, color: '#6B6560', marginTop: 2 }}>
                Range {fmtAVM(avm_low)}–{fmtAVM(avm_high)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
